import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { leadApi, teamApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Plus,
  Search,
  MoreVertical,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Building2,
  Layers,
  Eye,
  Pencil,
  Trash2,
  LayoutGrid,
  List,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  UserPlus,
  User,
} from 'lucide-react';
import { toast } from 'sonner';

const SALES_STAGES = ['Lead', 'Qualified', 'Estimate', 'Proposal', 'Negotiation', 'Won', 'Lost'];

const formatCurrency = (value) => {
  if (!value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value);
};

export default function Leads() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [leads, setLeads] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [groupByCompany, setGroupByCompany] = useState(false);
  const [stageFilter, setStageFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all' | 'mine'
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [followUpHours, setFollowUpHours] = useState(24); // Default 24 hours
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeads();
    fetchFollowUpHours();
    if (isOwner) {
      fetchManagers();
    }
  }, [isOwner]);

  const fetchLeads = async () => {
    try {
      const response = await leadApi.getAll();
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowUpHours = async () => {
    try {
      const response = await leadApi.getFollowUpHours();
      setFollowUpHours(response.data.follow_up_hours || 24);
    } catch (error) {
      console.log('Could not fetch follow-up hours setting');
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await teamApi.getMembers();
      // Assignable users = owner + managers (owner first)
      const assignable = response.data.filter(m => m.role === 'owner' || m.role === 'manager');
      assignable.sort((a, b) => (a.role === 'owner' ? -1 : b.role === 'owner' ? 1 : 0));
      setManagers(assignable);
    } catch (error) {
      console.log('Could not fetch managers');
    }
  };

  // Calculate lead follow-up status based on created_at and followUpHours setting
  // Returns: 'new' (within window - yellow), 'overdue' (past window - red), 'qualified' (no indicator)
  const getLeadFollowUpStatus = (lead) => {
    // If lead is already Qualified or beyond, no color indicator needed
    if (['Qualified', 'Estimate', 'Proposal', 'Negotiation', 'Won', 'Lost'].includes(lead.stage)) {
      return 'qualified';
    }
    
    const createdAt = new Date(lead.created_at);
    const now = new Date();
    const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);
    
    if (hoursSinceCreation <= followUpHours) {
      return 'new'; // Within follow-up window (yellow)
    } else {
      return 'overdue'; // Past follow-up window (red)
    }
  };

  // Get CSS classes for lead card based on follow-up status
  const getLeadCardClasses = (lead) => {
    const status = getLeadFollowUpStatus(lead);
    const baseClasses = 'pipeline-card cursor-pointer transition-all duration-200';
    
    switch (status) {
      case 'new':
        return `${baseClasses} border-l-4 border-l-yellow-500 bg-yellow-50/50`;
      case 'overdue':
        return `${baseClasses} border-l-4 border-l-red-500 bg-red-50/50`;
      default:
        return baseClasses;
    }
  };

  // Get text color for lead name based on follow-up status
  const getLeadNameColor = (lead) => {
    const status = getLeadFollowUpStatus(lead);
    switch (status) {
      case 'new':
        return 'text-yellow-700';
      case 'overdue':
        return 'text-red-700';
      default:
        return '';
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await leadApi.delete(id);
      setLeads(leads.filter((l) => l.id !== id));
      toast.success('Lead deleted');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await leadApi.updateStage(leadId, newStage);
      setLeads(leads.map((l) => (l.id === leadId ? { ...l, stage: newStage } : l)));
      toast.success(`Lead moved to ${newStage}`);
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  // Context-aware action for a Won lead:
  // - already converted -> view the existing project (1:1)
  // - has estimate(s) -> go to the lead to "Sell" the estimate (carries line items + contract value)
  // - otherwise -> open the Convert-to-Project form
  const handleWonAction = (lead, e) => {
    e?.stopPropagation();
    if (lead.project_id) {
      navigate(`/projects/${lead.project_id}`);
    } else if (lead.estimate_total > 0) {
      navigate(`/leads/${lead.id}`);
    } else {
      navigate(`/projects/new?lead_id=${lead.id}`);
    }
  };

  const getWonActionLabel = (lead) => {
    if (lead.project_id) return 'View Project';
    if (lead.estimate_total > 0) return 'Sell Estimate';
    return 'Convert to Project';
  };

  const handleAssign = async (leadId, managerId, e) => {
    e?.stopPropagation();
    try {
      await leadApi.assign(leadId, managerId || null);
      const manager = managers.find(m => m.id === managerId);
      setLeads(leads.map((l) => 
        l.id === leadId 
          ? { ...l, assigned_to: managerId || null, assigned_to_name: manager?.name || null } 
          : l
      ));
      toast.success(managerId ? `Lead assigned to ${manager?.name}` : 'Lead unassigned');
    } catch (error) {
      toast.error('Failed to assign lead');
    }
  };

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const response = await leadApi.importCsv(file);
      setImportResult(response.data);
      if (response.data.imported > 0) {
        fetchLeads();
        toast.success(`Successfully imported ${response.data.imported} leads`);
      }
      if (response.data.total_errors > 0) {
        toast.error(`${response.data.total_errors} rows had errors`);
      }
    } catch (error) {
      toast.error('Failed to import CSV');
      setImportResult({ imported: 0, errors: ['Upload failed'], total_errors: 1 });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const NO_COMPANY = '__none__';
  const companyOptions = Array.from(
    new Set(leads.map((l) => (l.company || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.name.toLowerCase().includes(search.toLowerCase()) ||
      lead.company?.toLowerCase().includes(search.toLowerCase()) ||
      lead.email?.toLowerCase().includes(search.toLowerCase()) ||
      lead.phone?.includes(search);
    const matchesStage = stageFilter === 'all' || lead.stage === stageFilter;
    const matchesAssignee = assigneeFilter === 'all' || lead.assigned_to === user?.id;
    const matchesCompany =
      companyFilter === 'all'
        ? true
        : companyFilter === NO_COMPANY
        ? !(lead.company && lead.company.trim())
        : (lead.company || '').trim() === companyFilter;
    return matchesSearch && matchesStage && matchesAssignee && matchesCompany;
  });

  const leadGroups = (() => {
    const map = new Map();
    filteredLeads.forEach((l) => {
      const key = l.company && l.company.trim() ? l.company.trim() : NO_COMPANY;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    });
    const arr = Array.from(map.entries()).map(([key, items]) => ({
      key,
      label: key === NO_COMPANY ? 'No Company / Individual' : key,
      items,
    }));
    arr.sort((a, b) => {
      if (a.key === NO_COMPANY) return 1;
      if (b.key === NO_COMPANY) return -1;
      return a.label.localeCompare(b.label);
    });
    return arr;
  })();

  const leadsByStage = SALES_STAGES.reduce((acc, stage) => {
    acc[stage] = filteredLeads.filter((l) => l.stage === stage);
    return acc;
  }, {});

  const renderLeadRow = (lead) => {
    const status = getLeadFollowUpStatus(lead);
    const rowBgClass = status === 'new' ? 'bg-yellow-50/50' : status === 'overdue' ? 'bg-red-50/50' : '';
    const nameColorClass = status === 'new' ? 'text-yellow-700 hover:text-yellow-900' : status === 'overdue' ? 'text-red-700 hover:text-red-900' : 'hover:text-orange-500';
    return (
      <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className={rowBgClass}>
        <td>
          <Link to={`/leads/${lead.id}`} className={`font-medium ${nameColorClass}`}>
            {lead.name}
          </Link>
          {lead.company && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <Building2 className="h-3 w-3" />
              {lead.company}
            </p>
          )}
          {lead.city && lead.state && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <MapPin className="h-3 w-3" />
              {lead.city}, {lead.state}
            </p>
          )}
        </td>
        <td>
          {lead.email && (
            <div className="flex items-center gap-1 text-sm">
              <Mail className="h-3 w-3" />
              {lead.email}
            </div>
          )}
          {lead.phone && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Phone className="h-3 w-3" />
              {lead.phone}
            </div>
          )}
        </td>
        <td>{lead.project_type || '-'}</td>
        <td className="font-medium">{formatCurrency(lead.estimated_value)}</td>
        <td>
          <Badge className={`status-${lead.stage.toLowerCase()}`}>
            {lead.stage}
          </Badge>
          {lead.assigned_to_name && (
            <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
              <User className="h-3 w-3" />
              {lead.assigned_to_name}
            </div>
          )}
        </td>
        <td>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid={`lead-actions-${lead.id}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}/edit`)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                Move to...
              </DropdownMenuItem>
              {SALES_STAGES.filter(s => s !== lead.stage).map((s) => (
                <DropdownMenuItem
                  key={s}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStageChange(lead.id, s);
                  }}
                >
                  {s}
                </DropdownMenuItem>
              ))}
              {isOwner && managers.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                    <UserPlus className="h-3 w-3 mr-2" />
                    Assign to...
                  </DropdownMenuItem>
                  {managers.map((manager) => (
                    <DropdownMenuItem
                      key={manager.id}
                      onClick={(e) => handleAssign(lead.id, manager.id, e)}
                      className={lead.assigned_to === manager.id ? 'bg-secondary' : ''}
                    >
                      <User className="h-3 w-3 mr-2" />
                      {manager.name}{manager.role === 'owner' ? ' (Owner)' : ''}
                      {lead.assigned_to === manager.id && ' ✓'}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-red-600"
                onClick={(e) => handleDelete(lead.id, e)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Leads</h1>
          <p className="text-muted-foreground text-sm">Manage your sales pipeline</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm text-sm" data-testid="import-leads-btn">
                <Upload className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Import CSV</span>
                <span className="sm:hidden">Import</span>
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Import Leads from CSV
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-secondary/50 rounded-sm">
                <p className="text-sm font-medium mb-2">CSV Format Requirements:</p>
                <p className="text-xs text-muted-foreground">
                  Your CSV should have columns for: <strong>name</strong> (required), email, phone, address, city, state, zip_code, source, project_type, estimated_value, notes
                </p>
              </div>
              <div className="border-2 border-dashed rounded-sm p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvImport}
                  ref={fileInputRef}
                  className="hidden"
                />
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="rounded-sm"
                >
                  {importing ? (
                    <><span className="loading-spinner mr-2" /> Importing...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Select CSV File</>
                  )}
                </Button>
              </div>
              {importResult && (
                <div className={`p-4 rounded-sm ${importResult.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className="text-sm font-medium">
                    {importResult.imported > 0 ? `✓ Imported ${importResult.imported} leads` : 'Import completed with errors'}
                  </p>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-red-600 font-medium flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Errors ({importResult.total_errors}):
                      </p>
                      <ul className="text-xs text-red-600 mt-1 space-y-1">
                        {importResult.errors.map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        <Link to="/leads/new">
          <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 btn-press text-sm" data-testid="new-lead-btn">
            <Plus className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add Lead</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="lead-search"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={assigneeFilter === 'mine' ? 'default' : 'outline'}
            className="rounded-sm"
            onClick={() => setAssigneeFilter(assigneeFilter === 'mine' ? 'all' : 'mine')}
            data-testid="my-leads-toggle"
          >
            <User className="h-4 w-4 mr-2" />
            My Leads
          </Button>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-[140px] sm:w-[180px] rounded-sm" data-testid="stage-filter">
              <SelectValue placeholder="Filter by stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {SALES_STAGES.map((stage) => (
                <SelectItem key={stage} value={stage}>
                  {stage}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="w-[150px] sm:w-[190px] rounded-sm" data-testid="lead-company-filter">
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <SelectValue placeholder="All Companies" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              <SelectItem value={NO_COMPANY}>No Company / Individual</SelectItem>
              {companyOptions.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {viewMode === 'list' && (
            <Button
              variant={groupByCompany ? 'default' : 'outline'}
              className="rounded-sm"
              onClick={() => setGroupByCompany((v) => !v)}
              data-testid="lead-group-toggle"
            >
              <Layers className="h-4 w-4 mr-2" />
              Group
            </Button>
          )}
          <div className="flex gap-1 border rounded-sm p-1">
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              className="rounded-sm"
              data-testid="kanban-view-btn"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-sm"
              data-testid="list-view-btn"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="pipeline-container" data-testid="kanban-view">
          {SALES_STAGES.filter((s) => s !== 'Won' && s !== 'Lost').map((stage) => (
            <div key={stage} className="pipeline-column">
              <div className="pipeline-column-header">
                <span>{stage}</span>
                <Badge variant="secondary" className="text-xs">
                  {leadsByStage[stage]?.length || 0}
                </Badge>
              </div>
              <div className="space-y-3 min-h-[200px]">
                {leadsByStage[stage]?.map((lead) => (
                  <div
                    key={lead.id}
                    className={getLeadCardClasses(lead)}
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    data-testid={`lead-card-${lead.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className={`font-medium text-sm ${getLeadNameColor(lead)}`}>{lead.name}</h4>
                        {lead.company && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Building2 className="h-3 w-3" />
                            {lead.company}
                          </p>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {SALES_STAGES.filter((s) => s !== lead.stage).map((s) => (
                            <DropdownMenuItem
                              key={s}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStageChange(lead.id, s);
                              }}
                            >
                              Move to {s}
                            </DropdownMenuItem>
                          ))}
                          {isOwner && managers.length > 0 && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                                <UserPlus className="h-3 w-3 mr-2" />
                                Assign to...
                              </DropdownMenuItem>
                              {managers.map((manager) => (
                                <DropdownMenuItem
                                  key={manager.id}
                                  onClick={(e) => handleAssign(lead.id, manager.id, e)}
                                  className={lead.assigned_to === manager.id ? 'bg-secondary' : ''}
                                >
                                  <User className="h-3 w-3 mr-2" />
                                  {manager.name}{manager.role === 'owner' ? ' (Owner)' : ''}
                                  {lead.assigned_to === manager.id && ' ✓'}
                                </DropdownMenuItem>
                              ))}
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={(e) => handleDelete(lead.id, e)}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {lead.project_type && (
                      <p className="text-xs text-muted-foreground mb-2">{lead.project_type}</p>
                    )}
                    {lead.assigned_to_name && (
                      <div className="flex items-center gap-1 text-xs text-blue-600 mb-1">
                        <User className="h-3 w-3" />
                        {lead.assigned_to_name}
                      </div>
                    )}
                    {lead.estimated_value && (
                      <div className="flex items-center gap-1 text-xs text-green-700">
                        <DollarSign className="h-3 w-3" />
                        {formatCurrency(lead.estimated_value)}
                      </div>
                    )}
                    {lead.phone && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Phone className="h-3 w-3" />
                        {lead.phone}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="rounded-sm" data-testid="list-view">
          <CardContent className="p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Project Type</th>
                  <th>Value</th>
                  <th>Stage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-muted-foreground">
                      No leads found
                    </td>
                  </tr>
                ) : groupByCompany ? (
                  leadGroups.map((g) => (
                    <React.Fragment key={g.key}>
                      <tr className="bg-secondary/40">
                        <td colSpan="6" className="py-2">
                          <span className="flex items-center gap-2 font-semibold text-sm" data-testid={`lead-group-${g.key}`}>
                            <Building2 className="h-4 w-4 text-orange-500" />
                            {g.label}
                            <Badge variant="secondary" className="rounded-sm">{g.items.length}</Badge>
                          </span>
                        </td>
                      </tr>
                      {g.items.map(renderLeadRow)}
                    </React.Fragment>
                  ))
                ) : (
                  filteredLeads.map(renderLeadRow)
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Won/Lost Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Won Leads */}
        <Card className="rounded-sm border-green-200">
          <CardHeader className="pb-2 bg-green-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-green-800">Won Leads</CardTitle>
              <Badge className="bg-green-600">{formatCurrency(
                leadsByStage['Won']?.reduce((sum, l) => sum + (l.estimated_value || 0), 0)
              )}</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-2">
            {leadsByStage['Won']?.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {leadsByStage['Won'].map((lead) => (
                  <div 
                    key={lead.id} 
                    className="flex items-center justify-between p-2 bg-green-50/50 rounded-sm hover:bg-green-100 cursor-pointer"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <div>
                      <p className="font-medium text-sm">{lead.name}</p>
                      <p className="text-xs text-muted-foreground">{lead.project_type}</p>
                    </div>
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-green-700 hover:bg-green-800"
                      onClick={(e) => handleWonAction(lead, e)}
                      data-testid={`won-action-${lead.id}`}
                    >
                      {getWonActionLabel(lead)}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No won leads yet</p>
            )}
          </CardContent>
        </Card>

        {/* Lost Leads */}
        <Card className="rounded-sm border-red-200">
          <CardHeader className="pb-2 bg-red-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-red-800">Lost Leads</CardTitle>
              <Badge className="bg-red-600">{formatCurrency(
                leadsByStage['Lost']?.reduce((sum, l) => sum + (l.estimated_value || 0), 0)
              )}</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-2">
            {leadsByStage['Lost']?.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {leadsByStage['Lost'].map((lead) => (
                  <div 
                    key={lead.id} 
                    className="flex items-center justify-between p-2 bg-red-50/50 rounded-sm hover:bg-red-100 cursor-pointer"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <div>
                      <p className="font-medium text-sm">{lead.name}</p>
                      <p className="text-xs text-muted-foreground">{lead.project_type}</p>
                    </div>
                    <Badge variant="outline" className="text-red-600 border-red-300">Lost</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No lost leads</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
